import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import {
  Button,
  Select,
  Spinner,
  Toast,
} from '../../components/design-system';
import { AdminPage } from './AdminPage';
import type { MatchStatus } from '../../types';
import {
  applyFilters, filtersFromParams, filtersToParams, journeysToCsv, stageCounts,
  STAGE_LABEL, SUB_LABEL, type Journey, type TripFilters,
} from '../../lib/tripPipeline';
import { TripDashboard } from '../../components/admin/TripDashboard';
import type { Period } from '../../lib/tripAnalytics';
import { FlowList, JourneyPanel, PipelineStrip, TripFilterBar } from '../../components/admin/TripPipelineViews';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TripRow {
  id: string;
  status: MatchStatus;
  archived_at: string | null;
  created_at: string;
  traveler_id: string;
  host_id: string;
  traveler_name: string | null;
  host_name: string | null;
  trip_name: string | null;
  city: string | null;
  itinerary_status: string | null;
  payment_status: string | null;
}

interface HostOption {
  profile_id: string;
  full_name: string | null;
  city: string | null;
}

// ── Status display — mirrors the labels used on the host/traveler side ────────

const STATUS_LABEL: Record<MatchStatus, string> = {
  proposed: 'Proposal',
  pending: 'Pending',
  accepted: 'Confirmed',
  declined: 'Declined',
  completed: 'Completed',
};

const STATUS_CLS: Record<MatchStatus, string> = {
  proposed: 'bg-app-bg text-app-fg-2 border-app-border',
  pending: 'bg-[rgba(255,153,59,.12)] text-gold border-[rgba(255,153,59,.24)]',
  accepted: 'bg-[rgba(111,63,253,.13)] text-violet border-[rgba(111,63,253,.26)]',
  declined: 'bg-crimson/10 text-crimson border-crimson/25',
  completed: 'bg-app-bg text-app-muted border-app-border',
};

function StatusChip({ status }: { status: MatchStatus }) {
  return (
    <span className={`inline-flex items-center rounded-pill border px-2.5 py-1 text-[11px] font-medium whitespace-nowrap ${STATUS_CLS[status]}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

const PAYMENT_LABEL: Record<string, string> = {
  paid: 'Paid', pending: 'Pending', failed: 'Failed', refunded: 'Refunded',
};

function SortableHeader({ label, column, sortColumn, sortDir, onSort }: {
  label: string; column: SortColumn; sortColumn: SortColumn; sortDir: 'asc' | 'desc'; onSort: (c: SortColumn) => void;
}) {
  const active = sortColumn === column;
  return (
    <th className="text-left px-4 py-3 font-medium text-app-muted">
      <button
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 hover:text-app-fg transition-colors ${active ? 'text-app-fg' : ''}`}
      >
        {label}
        <i className={`ti text-[13px] ${active ? (sortDir === 'asc' ? 'ti-arrow-up' : 'ti-arrow-down') : 'ti-arrows-sort'}`} />
      </button>
    </th>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

type SortColumn = 'trip' | 'status' | 'created' | 'host';

export function AdminTrips() {
  const { user } = useAuth();
  const [rows, setRows] = useState<TripRow[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | MatchStatus | 'archived'>('all');
  const [sortColumn, setSortColumn] = useState<SortColumn>('created');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [params, setParams] = useSearchParams();
  const viewParam = params.get('view');
  const view: 'list' | 'flow' | 'dashboard' = viewParam === 'flow' || viewParam === 'dashboard' ? viewParam : 'list';
  const period: Period = params.get('period') === '30' ? '30' : params.get('period') === 'all' ? 'all' : '90';
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [pipelineError, setPipelineError] = useState(false);
  const [openJourney, setOpenJourney] = useState<Journey | null>(null);
  function updateParams(mutate: (p: URLSearchParams) => void) {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  }
  function setFilters(patch: Partial<TripFilters>) {
    const next = filtersToParams({ ...filters, ...patch });
    if (view !== 'list') next.set('view', view);
    setParams(next, { replace: true });
  }
  function setView(v: 'list' | 'flow' | 'dashboard') {
    updateParams((p) => { if (v !== 'list') p.set('view', v); else p.delete('view'); });
  }
  const [toast, setToast] = useState('');
  function flashToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(''), 2400);
  }

  const [hosts, setHosts] = useState<HostOption[]>([]);
  const [reassignRow, setReassignRow] = useState<TripRow | null>(null);
  const [reassignHostId, setReassignHostId] = useState('');
  const [reassigning, setReassigning] = useState(false);

  const [detailPortraitRows, setDetailPortraitRows] = useState<TripRow[] | null>(null);
  const [detailFor, setDetailFor] = useState<TripRow | null>(null);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  async function load() {
    setDataLoading(true);
    setLoadError(false);

    // The pipeline is a separate read: if it fails the list below still works.
    const { data: pipe, error: pipeErr } = await supabase.rpc('admin_trip_pipeline');
    if (pipeErr) {
      console.error('AdminTrips: failed to load pipeline', pipeErr);
      setPipelineError(true);
      setJourneys([]);
    } else {
      setPipelineError(false);
      setJourneys((pipe ?? []) as Journey[]);
    }

    const { data: matches, error: matchesError } = await supabase
      .from('matches')
      .select('id, status, archived_at, created_at, traveler_id, host_id, portrait_id')
      .order('created_at', { ascending: false });

    // A failed select (e.g. a column the running migration hasn't reached
    // yet) used to fall straight through to an empty array here — the page
    // rendered "No trips" with zero indication anything had gone wrong, no
    // differently than there genuinely being no trips.
    if (matchesError) {
      console.error('AdminTrips: failed to load matches', matchesError);
      setLoadError(true);
      setRows([]);
      setDataLoading(false);
      return;
    }

    const matchRows = (matches ?? []) as {
      id: string; status: MatchStatus; archived_at: string | null; created_at: string;
      traveler_id: string; host_id: string; portrait_id: string | null;
    }[];

    if (matchRows.length === 0) { setRows([]); setDataLoading(false); return; }

    const matchIds = matchRows.map((m) => m.id);
    const profileIds = [...new Set(matchRows.flatMap((m) => [m.traveler_id, m.host_id]))];
    const portraitIds = [...new Set(matchRows.map((m) => m.portrait_id).filter((x): x is string => !!x))];

    const [{ data: profiles }, { data: portraits }, { data: itineraries }, { data: payments }] = await Promise.all([
      supabase.from('profiles').select('id, full_name').in('id', profileIds),
      portraitIds.length
        ? supabase.from('taste_portraits').select('id, trip_name, destinations').in('id', portraitIds)
        : Promise.resolve({ data: [] }),
      supabase.from('itineraries').select('match_id, status, content').in('match_id', matchIds),
      supabase.from('payments').select('match_id, status').in('match_id', matchIds),
    ]);

    const nameMap = new Map(((profiles ?? []) as { id: string; full_name: string | null }[]).map((p) => [p.id, p.full_name]));
    const portraitMap = new Map(
      ((portraits ?? []) as { id: string; trip_name: string | null; destinations: string[] | null }[]).map((p) => [p.id, p]),
    );
    const itinMap = new Map(
      ((itineraries ?? []) as { match_id: string; status: string; content: Record<string, unknown> }[]).map((i) => [i.match_id, i]),
    );
    const paymentMap = new Map(
      ((payments ?? []) as { match_id: string; status: string }[]).map((p) => [p.match_id, p.status]),
    );

    setRows(matchRows.map((m) => {
      const portrait = m.portrait_id ? portraitMap.get(m.portrait_id) : null;
      const itin = itinMap.get(m.id);
      const content = itin?.content as { trip_name?: string; destination?: string } | undefined;
      return {
        id: m.id,
        status: m.status,
        archived_at: m.archived_at,
        created_at: m.created_at,
        traveler_id: m.traveler_id,
        host_id: m.host_id,
        traveler_name: nameMap.get(m.traveler_id) ?? null,
        host_name: nameMap.get(m.host_id) ?? null,
        trip_name: content?.trip_name || portrait?.trip_name || null,
        city: content?.destination || portrait?.destinations?.join(', ') || null,
        itinerary_status: itin?.status ?? null,
        payment_status: paymentMap.get(m.id) ?? null,
      };
    }));
    setDataLoading(false);
  }

  useEffect(() => { if (user) void load(); }, [user]);

  // Selection is scoped to what's currently visible — switching filters with
  // a stale selection from a different tab silently referred to rows the
  // admin can no longer even see.
  useEffect(() => { setSelectedIds(new Set()); }, [statusFilter]);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      const { data: hp } = await supabase.from('host_profiles').select('profile_id, city').eq('active', true);
      const hpRows = (hp as { profile_id: string; city: string | null }[]) ?? [];
      if (hpRows.length === 0) { setHosts([]); return; }
      const ids = hpRows.map((r) => r.profile_id);
      const { data: profs } = await supabase.from('profiles').select('id, full_name').in('id', ids);
      const nameMap = new Map(((profs as { id: string; full_name: string | null }[]) ?? []).map((p) => [p.id, p.full_name]));
      setHosts(hpRows.map((r) => ({ profile_id: r.profile_id, full_name: nameMap.get(r.profile_id) ?? null, city: r.city })));
    })();
  }, [user]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: 0, archived: 0 };
    for (const r of rows) {
      if (r.archived_at) { c.archived++; continue; }
      c.all++;
      c[r.status] = (c[r.status] ?? 0) + 1;
    }
    return c;
  }, [rows]);

  function toggleSort(column: SortColumn) {
    if (sortColumn === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDir(column === 'created' ? 'desc' : 'asc');
    }
  }

  const visibleRows = useMemo(() => {
    const byStatus = statusFilter === 'archived'
      ? rows.filter((r) => !!r.archived_at)
      : rows
        .filter((r) => !r.archived_at)
        .filter((r) => statusFilter === 'all' || r.status === statusFilter);
    // Shared filters narrow the list too; visibility toggles (abandoned / exited /
    // archived) belong to Flow, the list keeps its own status dropdown for those.
    const allowed = pipelineError ? null : new Set(applyFilters(journeys, { ...filters, stage: filters.stage, showAbandoned: true, showExited: true, showArchived: true }).map((j) => j.match_id));
    const filtered = allowed ? byStatus.filter((r) => allowed.has(r.id)) : byStatus;

    const dir = sortDir === 'asc' ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      switch (sortColumn) {
        case 'trip':
          return dir * (a.trip_name ?? 'Untitled trip').localeCompare(b.trip_name ?? 'Untitled trip');
        case 'status':
          return dir * STATUS_LABEL[a.status].localeCompare(STATUS_LABEL[b.status]);
        case 'host':
          return dir * (a.host_name ?? '').localeCompare(b.host_name ?? '');
        case 'created':
        default:
          return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      }
    });
    return sorted;
  }, [rows, statusFilter, sortColumn, sortDir, journeys, filters, pipelineError]);

  // ── Archive / unarchive ──
  async function toggleArchive(row: TripRow) {
    setBusyId(row.id);
    const { error } = await supabase
      .from('matches')
      .update({ archived_at: row.archived_at ? null : new Date().toISOString() })
      .eq('id', row.id);
    setBusyId(null);
    if (error) { flashToast('Could not update this trip. Please try again.'); return; }
    flashToast(row.archived_at ? 'Trip restored.' : 'Trip archived.');
    void load();
  }

  // ── Delete (payment-guarded server-side too — this is a courtesy hint, not the enforcement) ──
  async function handleDelete(row: TripRow) {
    if (row.payment_status) return; // guarded in the UI; RPC guards it again server-side
    if (!window.confirm(`Permanently delete "${row.trip_name ?? 'this trip'}"? This can't be undone.`)) return;
    setBusyId(row.id);
    const { error } = await supabase.rpc('admin_delete_match', { p_match_id: row.id });
    setBusyId(null);
    if (error) { flashToast(error.message || 'Could not delete this trip.'); return; }
    flashToast('Trip deleted.');
    void load();
  }

  // ── Multiselect delete ──
  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAllVisible() {
    setSelectedIds((prev) => {
      const allSelected = visibleRows.length > 0 && visibleRows.every((r) => prev.has(r.id));
      if (allSelected) return new Set();
      return new Set(visibleRows.map((r) => r.id));
    });
  }

  async function handleBulkDelete() {
    const targets = rows.filter((r) => selectedIds.has(r.id));
    const deletable = targets.filter((r) => !r.payment_status);
    const skipped = targets.length - deletable.length;
    if (deletable.length === 0) {
      flashToast(skipped > 0 ? "Selected trips can't be deleted — they have a payment on record." : 'Nothing selected.');
      return;
    }
    const confirmMsg = skipped > 0
      ? `Permanently delete ${deletable.length} trip${deletable.length === 1 ? '' : 's'}? ${skipped} paid trip${skipped === 1 ? '' : 's'} will be skipped (archive those instead). This can't be undone.`
      : `Permanently delete ${deletable.length} trip${deletable.length === 1 ? '' : 's'}? This can't be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setBulkDeleting(true);
    const results = await Promise.all(
      deletable.map((r) => supabase.rpc('admin_delete_match', { p_match_id: r.id })),
    );
    setBulkDeleting(false);

    const failures = results.filter((r) => r.error).length;
    const succeeded = deletable.length - failures;
    if (failures === 0) {
      flashToast(`${succeeded} trip${succeeded === 1 ? '' : 's'} deleted${skipped > 0 ? ` (${skipped} paid trip${skipped === 1 ? '' : 's'} skipped)` : ''}.`);
    } else {
      flashToast(`${succeeded} deleted, ${failures} failed. Please retry the failed ones.`);
    }
    setSelectedIds(new Set());
    void load();
  }

  // ── Reassign host ──
  function openReassign(row: TripRow) {
    setReassignRow(row);
    setReassignHostId('');
  }

  async function confirmReassign() {
    if (!reassignRow || !reassignHostId || reassigning) return;
    setReassigning(true);
    const { error } = await supabase.rpc('admin_reassign_host', {
      p_match_id: reassignRow.id,
      p_new_host_id: reassignHostId,
    });
    setReassigning(false);
    if (error) { flashToast(error.message || 'Could not reassign this trip.'); return; }
    flashToast('Host reassigned.');
    setReassignRow(null);
    void load();
  }

  // ── Match details (the "target" icon) — every match sharing this portrait ──
  function openDetails(row: TripRow) {
    setDetailFor(row);
    if (!row.trip_name && !rows.some((r) => r.id !== row.id)) { setDetailPortraitRows([row]); return; }
    setDetailPortraitRows(rows.filter((r) => r.traveler_id === row.traveler_id && r.trip_name === row.trip_name));
  }

  const journeyByMatch = useMemo(() => new Map(journeys.filter((j) => j.match_id).map((j) => [j.match_id as string, j])), [journeys]);
  const flowRows = useMemo(() => applyFilters(journeys, filters), [journeys, filters]);
  const counts2 = useMemo(() => stageCounts(journeys, filters), [journeys, filters]);
  const hostFilterOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const j of journeys) if (j.host_id) m.set(j.host_id, j.host_name ?? j.host_id.slice(0, 8));
    return [...m].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [journeys]);
  const cityOptions = useMemo(
    () => [...new Set(journeys.map((j) => j.city).filter((c): c is string => !!c))].sort(),
    [journeys],
  );
  function exportCsv() {
    const blob = new Blob([journeysToCsv(flowRows)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `trips-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <AdminPage title="Trips" description="Every trip in the pipeline — proposals, active, completed, and archived.">
      <div className="mb-4 inline-flex rounded-lg border border-app-border bg-app-card p-0.5 text-xs">
        {(['list', 'flow', 'dashboard'] as const).map((v) => (
          <button
            key={v} type="button" onClick={() => setView(v)} aria-pressed={view === v}
            className={`rounded-md px-3 py-1.5 font-medium ${view === v ? 'bg-violet text-white' : 'text-app-muted hover:text-app-fg'}`}
          >
            {v === 'list' ? 'List' : v === 'flow' ? 'Flow' : 'Dashboard'}
          </button>
        ))}
      </div>

      {view === 'dashboard' && !pipelineError ? (
        dataLoading ? <div className="flex justify-center py-12"><Spinner /></div>
          : <TripDashboard journeys={journeys} period={period} onPeriod={(p) => updateParams((q) => q.set('period', p))} onOpen={setOpenJourney} />
      ) : (<>
      {pipelineError ? (
        <div className="mb-4 rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 text-xs text-crimson">
          Couldn&apos;t load the pipeline. The list below still works.
        </div>
      ) : (
        <>
          {view === 'flow' && <PipelineStrip counts={counts2} active={filters.stage} onPick={(s) => setFilters({ stage: s })} />}
          <TripFilterBar
            f={filters} set={setFilters} hosts={hostFilterOptions} cities={cityOptions}
            total={view === 'flow' ? flowRows.length : visibleRows.length} onExport={exportCsv}
          />
        </>
      )}

      {view === 'flow' && !pipelineError ? (
        dataLoading ? <div className="flex justify-center py-12"><Spinner /></div>
          : <FlowList rows={flowRows} onOpen={setOpenJourney} />
      ) : (<>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          options={[
            { value: 'all', label: `All (${counts.all})` },
            { value: 'proposed', label: `Proposal (${counts.proposed ?? 0})` },
            { value: 'pending', label: `Pending (${counts.pending ?? 0})` },
            { value: 'accepted', label: `Confirmed (${counts.accepted ?? 0})` },
            { value: 'completed', label: `Completed (${counts.completed ?? 0})` },
            { value: 'declined', label: `Declined (${counts.declined ?? 0})` },
            { value: 'archived', label: `Archived (${counts.archived})` },
          ]}
          style={{ width: 220 }}
        />
        <span className="text-xs text-app-muted">{visibleRows.length} trip{visibleRows.length === 1 ? '' : 's'}</span>
      </div>

      {selectedIds.size > 0 && (
        <div className="mb-4 rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 flex items-center justify-between gap-3">
          <span className="text-xs text-app-fg">{selectedIds.size} trip{selectedIds.size === 1 ? '' : 's'} selected</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} disabled={bulkDeleting}>Clear</Button>
            <Button size="sm" onClick={() => void handleBulkDelete()} disabled={bulkDeleting} className="gap-1.5 bg-crimson hover:bg-crimson/90">
              {bulkDeleting ? <Spinner size="sm" /> : <i className="ti ti-trash text-[14px]" />}
              Delete selected
            </Button>
          </div>
        </div>
      )}

      {loadError && (
        <div className="mb-4 rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 flex items-center justify-between gap-3">
          <span className="text-xs text-crimson">Couldn&apos;t load trips — something went wrong talking to the database.</span>
          <Button size="sm" variant="ghost" onClick={() => void load()}>Retry</Button>
        </div>
      )}

      {dataLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-app-border bg-app-card shadow-sm">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-app-border bg-app-bg">
                <th className="text-left px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all visible trips"
                    checked={visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(r.id))}
                    ref={(el) => {
                      if (el) el.indeterminate = selectedIds.size > 0 && !visibleRows.every((r) => selectedIds.has(r.id));
                    }}
                    onChange={toggleSelectAllVisible}
                    className="w-3.5 h-3.5 accent-crimson cursor-pointer"
                  />
                </th>
                <SortableHeader label="Trip" column="trip" sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader label="Status" column="status" sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader label="Created" column="created" sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} />
                <SortableHeader label="Host" column="host" sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} />
                <th className="text-left px-4 py-3 font-medium text-app-muted">Stage</th>
                <th className="text-left px-4 py-3 font-medium text-app-muted">Payment</th>
                <th className="text-left px-4 py-3 font-medium text-app-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-app-muted text-xs">No trips.</td></tr>
              )}
              {visibleRows.map((row) => {
                const canDelete = !row.payment_status;
                const isBusy = busyId === row.id;
                return (
                  <tr key={row.id} className={`border-b border-app-border last:border-0 hover:bg-app-bg/50 ${selectedIds.has(row.id) ? 'bg-crimson/[.04]' : ''}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.trip_name || 'this trip'}`}
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleSelected(row.id)}
                        className="w-3.5 h-3.5 accent-crimson cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-app-fg text-xs font-medium">{row.trip_name || 'Untitled trip'}</div>
                      <div className="flex items-center gap-1 mt-0.5 text-[11px] text-app-muted">
                        <i className="ti ti-user text-[10px]" />
                        {row.traveler_name ?? row.traveler_id.slice(0, 8)}
                        {row.city && <><span className="mx-0.5">·</span><i className="ti ti-map-pin text-[10px]" />{row.city}</>}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusChip status={row.status} /></td>
                    <td className="px-4 py-3 text-app-muted text-xs">{new Date(row.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-app-fg text-xs">
                      {row.host_name ?? <span className="text-app-muted font-mono">{row.host_id.slice(0, 8)}…</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {(() => {
                        const jr = journeyByMatch.get(row.id);
                        if (!jr) return <span className="text-app-muted">—</span>;
                        return (
                          <button type="button" onClick={() => setOpenJourney(jr)} className="text-left hover:underline">
                            <div className="text-app-fg">{STAGE_LABEL[jr.stage]}</div>
                            <div className="text-[11px] text-app-muted">{SUB_LABEL[jr.sub_state] ?? jr.sub_state}{jr.flags.length > 0 ? ' · ⚠' : ''}</div>
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {row.payment_status
                        ? <span className={row.payment_status === 'paid' ? 'text-success' : 'text-app-muted'}>{PAYMENT_LABEL[row.payment_status] ?? row.payment_status}</span>
                        : <span className="text-app-muted">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/admin/trips/${row.id}`}
                          title="View as host (read-only)"
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-app-muted hover:text-app-fg hover:bg-app-bg transition-colors"
                        >
                          <i className="ti ti-eye text-[15px]" />
                        </Link>
                        <button
                          title="See current match"
                          onClick={() => openDetails(row)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-app-muted hover:text-app-fg hover:bg-app-bg transition-colors"
                        >
                          <i className="ti ti-target text-[15px]" />
                        </button>
                        <button
                          title="Assign / replace host"
                          onClick={() => openReassign(row)}
                          disabled={isBusy}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-app-muted hover:text-app-fg hover:bg-app-bg transition-colors disabled:opacity-40"
                        >
                          <i className="ti ti-user-plus text-[15px]" />
                        </button>
                        <button
                          title={row.archived_at ? 'Restore trip' : 'Archive trip'}
                          onClick={() => void toggleArchive(row)}
                          disabled={isBusy}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-app-muted hover:text-app-fg hover:bg-app-bg transition-colors disabled:opacity-40"
                        >
                          <i className={`ti ${row.archived_at ? 'ti-archive-off' : 'ti-archive'} text-[15px]`} />
                        </button>
                        <button
                          title={canDelete ? 'Delete permanently' : "Paid trips can't be deleted — archive instead"}
                          onClick={() => void handleDelete(row)}
                          disabled={!canDelete || isBusy}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-crimson hover:bg-crimson/10 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:text-app-muted"
                        >
                          <i className="ti ti-trash text-[15px]" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      </>)}
      </>)}

      {openJourney && <JourneyPanel j={openJourney} onClose={() => setOpenJourney(null)} />}

      {/* ── Reassign host modal ── */}
      {reassignRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setReassignRow(null)}>
          <div className="bg-app-card border border-app-border rounded-2xl shadow-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-app-border">
              <h2 className="text-base font-semibold text-app-fg">Assign a different host</h2>
              <p className="text-xs text-app-muted mt-1">
                {reassignRow.trip_name || 'This trip'} — currently {reassignRow.host_name ?? 'unassigned'}.
                The existing match is declined and a new one is created for the host you pick;
                any payment already taken moves with it.
              </p>
            </div>
            <div className="px-6 py-5">
              <Select
                label="New host"
                value={reassignHostId}
                onChange={(e) => setReassignHostId(e.target.value)}
                placeholder="Choose a host…"
                options={hosts
                  .filter((h) => h.profile_id !== reassignRow.host_id)
                  .map((h) => ({ value: h.profile_id, label: h.full_name ? `${h.full_name}${h.city ? ` · ${h.city}` : ''}` : h.profile_id }))}
              />
            </div>
            <div className="px-6 py-4 border-t border-app-border flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setReassignRow(null)}>Cancel</Button>
              <Button onClick={() => void confirmReassign()} disabled={!reassignHostId || reassigning}>
                {reassigning ? <Spinner size="sm" /> : 'Reassign'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Match details modal (target icon) ── */}
      {detailFor && detailPortraitRows && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 px-4 py-8 overflow-y-auto" onClick={() => setDetailFor(null)}>
          <div className="bg-app-card border border-app-border rounded-2xl shadow-xl w-full max-w-lg my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-app-border">
              <h2 className="text-base font-semibold text-app-fg">Current match</h2>
              <button onClick={() => setDetailFor(null)} className="w-8 h-8 rounded-lg flex items-center justify-center text-app-muted hover:text-app-fg hover:bg-black/5 transition-colors">
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="px-6 py-5 flex flex-col gap-3">
              <p className="text-xs text-app-muted">
                Every match proposed for {detailFor.traveler_name ?? 'this traveler'}&apos;s &quot;{detailFor.trip_name ?? 'this trip'}&quot;.
              </p>
              {detailPortraitRows.map((r) => (
                <div key={r.id} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${r.id === detailFor.id ? 'border-gold bg-gold/5' : 'border-app-border'}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-app-fg font-medium truncate">{r.host_name ?? r.host_id.slice(0, 8)}</div>
                    <div className="text-[11px] text-app-muted">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <StatusChip status={r.status} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Toast message={toast} />
    </AdminPage>
  );
}

export default AdminTrips;
